import { useEffect, useState } from "react";
import superAdminApi from "../../../lib/superAdminApi";
import SuperAdminLayout from "../../../components/SuperAdminLayout";
import { Users, Building2, Store, CreditCard, TrendingUp, UserCheck } from "lucide-react";

interface Stats {
  totalUsers: number;
  totalBusinesses: number;
  totalStores: number;
  totalSubscriptions: number;
  activeSubscriptions: number;
  totalSales: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  signupTrend: { date: string; count: number }[];
}

const StatCard = ({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number | string;
  icon: any;
  color: string;
}) => (
  <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm flex items-center gap-4">
    <div className={`p-3 rounded-lg ${color}`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
    </div>
  </div>
);

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    superAdminApi
      .get("/superadmin/stats")
      .then((res) => setStats(res.data))
      .catch((err) => setError(err.response?.data?.message ?? "Failed to load stats"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <SuperAdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Overview</h1>
          <p className="text-gray-500 text-sm mt-1">Platform-wide monitoring dashboard</p>
        </div>

        {error && (
          <div className="px-4 py-3 rounded-lg bg-red-50 text-red-600 border border-red-100 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm flex items-center gap-4 animate-pulse">
                  <div className="w-11 h-11 rounded-lg bg-gray-200 flex-shrink-0" />
                  <div className="space-y-2 flex-1">
                    <div className="h-3 bg-gray-200 rounded w-24" />
                    <div className="h-6 bg-gray-200 rounded w-16" />
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-48 mb-5" />
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex gap-8">
                    <div className="h-3 bg-gray-200 rounded w-24" />
                    <div className="h-3 bg-gray-200 rounded w-8" />
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : stats ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              <StatCard label="Total Users" value={stats.totalUsers} icon={Users} color="bg-blue-500" />
              <StatCard label="Total Businesses" value={stats.totalBusinesses} icon={Building2} color="bg-purple-500" />
              <StatCard label="Total Stores" value={stats.totalStores} icon={Store} color="bg-emerald-500" />
              <StatCard label="Active Subscriptions" value={stats.activeSubscriptions} icon={CreditCard} color="bg-amber-500" />
              <StatCard label="New Users Today" value={stats.newUsersToday} icon={UserCheck} color="bg-indigo-500" />
              <StatCard label="New Users This Week" value={stats.newUsersThisWeek} icon={TrendingUp} color="bg-rose-500" />
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
              <h2 className="text-base font-semibold text-gray-800 mb-4">Signups — Last 30 Days</h2>
              {stats.signupTrend.length === 0 ? (
                <p className="text-sm text-gray-400">No signups in the last 30 days.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-100">
                        <th className="pb-2 font-medium">Date</th>
                        <th className="pb-2 font-medium">New Users</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {[...stats.signupTrend]
                        .sort((a, b) => b.date.localeCompare(a.date))
                        .map((row) => (
                          <tr key={row.date}>
                            <td className="py-2 text-gray-700">{row.date}</td>
                            <td className="py-2 font-medium text-gray-900">{row.count}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminDashboard;
